import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import InputField from './InputField';
import SelectField from './SelectField';
import TextAreaField from './TextAreaField';
import CheckboxField from './CheckboxField';
import DateField from './DateField';
import TimeField from './TimeField';
import PhoneNumberField from './PhoneField';

interface DynamicFieldProps {
  field: CustomFormField;
}

const DynamicField = ({ field }: DynamicFieldProps): ReactElement => {
  const router = useRouter();
  const isSpanish = router.locale === 'es';

  const getValidationRules = () => {
    const rules = field.validation_rules || {};
    return {
      minLength: rules.min_length,
      maxLength: rules.max_length,
      min: rules.min_value,
      max: rules.max_value,
      pattern: rules.pattern,
    };
  };

  const validationRules = getValidationRules();

  switch (field.field_type) {
    case 'text':
      return (
        <InputField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          labeled={true}
          type="text"
          minLength={validationRules.minLength}
          maxLength={validationRules.maxLength}
        />
      );

    case 'email':
      return (
        <InputField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder || 'ejemplo@correo.com'}
          required={field.required}
          labeled={true}
          type="email"
        />
      );

    case 'number':
      return (
        <InputField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          labeled={true}
          type="number"
          min={validationRules.min}
          max={validationRules.max}
        />
      );

    case 'select':
      return (
        <SelectField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder || (isSpanish ? '-- Seleccionar --' : '-- Select --')}
          options={field.options || []}
          required={field.required}
          labeled={true}
        />
      );

    case 'textarea':
      return (
        <TextAreaField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          labeled={true}
          maxLength={validationRules.maxLength}
        />
      );

    case 'checkbox':
      return (
        <CheckboxField
          name={field.name}
          label={field.label}
          required={field.required}
        />
      );

    case 'date':
      return (
        <DateField
          name={field.name}
          label={field.label}
          required={field.required}
          labeled={true}
        />
      );

    case 'time':
      return (
        <TimeField
          name={field.name}
          label={field.label}
          required={field.required}
          labeled={true}
        />
      );

    case 'phone':
      return (
        <PhoneNumberField
          name={field.name}
          label={field.label}
          labeled={true}
        />
      );

    default:
      return (
        <InputField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          labeled={true}
          type="text"
        />
      );
  }
};

export default DynamicField;